// BuildingsTable.js
import React from 'react';
import { Card, Table, Button, Form, Modal, Carousel } from 'react-bootstrap';
import { BiBuilding } from 'react-icons/bi';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import './BuildingsTable.css';

function getSystemStyle(type) {
  const lowerType = type.toLowerCase();
  if (lowerType === 'fancoil') {
    // Light/pastel blue
    return { backgroundColor: '#d6e6ff', color: '#333' };
  } else if (lowerType === 'heatpump') {
    // Light/pastel orange
    return { backgroundColor: '#ffe5cc', color: '#333' };
  } else if (lowerType === 'erv') {
    // Light/pastel green
    return { backgroundColor: '#d4fdd4', color: '#333' };
  } else {
    // Fallback style
    return { backgroundColor: '#edf2ff', color: '#224982' };
  }
}

function BuildingsTable({
  pageData,
  sortedData,
  startIndex,
  endIndex,
  currentPage,
  totalPages,
  rowsPerPage,
  sortColumn,
  sortDirection,
  handleSort,
  handlePrevPage,
  handleNextPage,
  handleRowsPerPageChange,
  handleOpenCarousel,
  showModal,
  carouselPhotos,
  carouselIndex,
  handleCloseModal,
  handleSelectCarousel,
  isVideoUrl,
  handleShowInvite, // passed down from parent (RegionalDashboard)
}) {
  // Helper to show sort arrow for a given column
  const getSortArrow = (columnKey) => {
    if (sortColumn === columnKey) {
      return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Local handler for the "Invite Manager" button
  const handleInviteClick = () => {
    if (typeof handleShowInvite === 'function') {
      handleShowInvite();
    } else {
      console.warn('handleShowInvite callback is not provided.');
    }
  };
  

  const noData = pageData.length === 0;

  return (
    <div 
      data-tour="buildings-table"
      className="buildings-table-wrapper"
      style={{ width: '90%', margin: '0 auto 2rem' }}
    >
      <Card className="shadow-sm border-0 buildings-table-card">
        <Card.Body style={{ padding: 0 }}>
          <Table hover responsive className="buildings-table mb-0 border-0">
            <thead>
              <tr>
                <th onClick={() => handleSort('status')} className="clickable-th">
                  Status {getSortArrow('status')}
                </th>
                <th>Picture</th>
                <th onClick={() => handleSort('name')} className="clickable-th">
                  Name {getSortArrow('name')}
                </th>
                <th onClick={() => handleSort('manager')} className="clickable-th">
                  Manager {getSortArrow('manager')}
                </th>
                <th onClick={() => handleSort('units')} className="clickable-th">
                  Units {getSortArrow('units')}
                </th>
                <th onClick={() => handleSort('system')} className="clickable-th">
                  HVAC System {getSortArrow('system')}
                </th>
                <th onClick={() => handleSort('mostRecentMaintenance')} className="clickable-th">
                  Most Recent Maintenance {getSortArrow('mostRecentMaintenance')}
                </th>
                <th>Report</th>
              </tr>
            </thead>

            <tbody>
              {noData ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                    <BiBuilding size={48} color="#1f5eec" />
                    <h4
                      style={{
                        marginTop: '1rem',
                        marginBottom: '1rem',
                        fontSize: '1.25rem',
                        color: '#333',
                      }}
                    >
                      None of your buildings use Hudson for Preventive Maintenance.<br />Start by inviting a manager
                    </h4>
                    <Button
                      className="invite-button"
                      onClick={handleInviteClick}
                      style={{
                        backgroundColor: '#1f5eec',
                        border: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#fff',
                        padding: '0.75rem 1.25rem',
                        transition: 'background 0.2s ease',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#1854c7';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#1f5eec';
                      }}
                    >
                      + Invite Manager
                    </Button>
                  </td>
                </tr>
              ) : (
                pageData.map((row, idx) => {
                  // Decide which badge to display based on row.status
                  let statusBadge;
                  if (row.status === 'Active') {
                    statusBadge = <span className="badge-active">Active</span>;
                  } else if (row.status === 'Pending') {
                    statusBadge = (
                      <OverlayTrigger
                        key={`pending-tooltip-${idx}`}
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-pending-${idx}`}>
                            We’re in the process of onboarding this building and are awaiting Board approval
                          </Tooltip>
                        }
                      >
                        <span className="badge-invite" style={{ cursor: 'pointer' }}>
                          Pending
                        </span>
                      </OverlayTrigger>
                    );
                  }
                   else {
                    statusBadge = row.status || 'Unknown';
                  }

                  return (
                    <tr key={idx}>
                      {/* Status Column */}
                      <td>{statusBadge}</td>

                      {/* Picture */}
                      <td className="photos-cell">
                        {(row.photo_url || (row.photo && row.photo.url)) ? (
                          <img
                            src={row.photo_url || row.photo.url}
                            alt={row.name}
                            className="file-thumbnail image-thumbnail"
                            style={{ maxWidth: '100px' }}
                          />
                        ) : '-'}
                      </td>

                      {/* Name */}
                      <td>{row.name || '-'}</td>

                      {/* Manager */}
                      <td>{row.manager || '-'}</td>

                      {/* Units */}
                      <td>{row.units || '-'}</td>

                      {/* HVAC System */}
                      <td>
                        {Array.isArray(row.system) && row.system.length > 0 ? (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {row.system.map((type, i) => {
                              const style = {
                                display: 'inline-block',
                                padding: '0.35rem 0.75rem',
                                borderRadius: '9999px',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                ...getSystemStyle(type), // apply custom color
                              };
                              return (
                                <div key={i} style={style}>
                                  {type}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>

                      {/* Most Recent Maintenance */}
                      <td>{row.mostRecentMaintenance || '-'}</td>

                      {/* Report Button */}
                      <td>
                        {row.finalReport ? (
                          <Button
                            className="issues-button"
                            size="sm"
                            variant="primary"
                            onClick={() => window.open(row.finalReport, '_blank')}
                          >
                            View Report
                          </Button>
                        ) : row.status === 'Active' ? (
                          <Button
                            className="issues-button"
                            size="sm"
                            disabled
                            style={{
                              backgroundColor: '#e9e9e9', // a lighter gray
                              color: '#222',
                              border: 'none',
                            }}
                          >
                            Maintenance Pending
                          </Button>

                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="8">
                  <div className="buildings-pagination d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2">Rows per page:</span>
                      <Form.Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        className="rows-per-page-select"
                        style={{ width: 'auto' }}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                      </Form.Select>
                      <span className="ms-3">
                        Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of{' '}
                        {sortedData.length}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="me-3">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>

      {/* Slideshow Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Body className="p-0">
          <Carousel activeIndex={carouselIndex} onSelect={handleSelectCarousel} interval={null}>
            {carouselPhotos.map((url, i) => (
              <Carousel.Item key={i}>
                {isVideoUrl(url) ? (
                  <video controls src={url} className="d-block w-100 carousel-video" />
                ) : (
                  <img src={url} alt="Building Preview" className="d-block w-100 carousel-image" />
                )}
              </Carousel.Item>
            ))}
          </Carousel>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default BuildingsTable;
